/**
 * sole-prop-config.ts — constants for the Sole Proprietor cohort wizard.
 *
 * Source of truth: memory `project_business_procedures_full_lock_20260506`.
 *
 * Sole Proprietor is the **Personal-with-DBA-overlay** cohort — structurally
 * a Personal CIF (one CIF, owner's SSN, Field 39=Y Consumer, POD allowed) with
 * a DBA name overlay applied to the Account-level fields (Field 84) so the
 * business "name" prints on statements and checks while the underlying CIF
 * stays in the owner's name.
 *
 * HARD WIRE LOCKS (do not parametrize without memo update):
 *   - customerType = 'Individual' (long form; matches Personal cohort)
 *   - callClass    = '26'  (Personal DBA partition — distinct from Personal's 0)
 *   - cifField39   = 'Y'   (Consumer)
 *   - tinCode      = 'S'   (SSN; Sole Prop uses owner's SSN, NOT EIN)
 *   - cipField254  = '6'   (Fictitious Name Filing as ID source)
 *   - Field 84     = `<Business Name> DBA`  (e.g., "Bill's Bait Shop DBA")
 *
 * Affordances:
 *   - POD beneficiaries ALLOWED (unlike LLC/Partnership/Corp).
 *   - NO Beneficial Owner form.
 *   - NO Banking Resolution.
 *   - Two auxiliary CIFs allowed (Debit Card cardholder, NetTeller email).
 */

// -----------------------------------------------------------------------------
// Wire mappings — every value here is locked per the procedure memo
// -----------------------------------------------------------------------------

export const SOLE_PROP_WIRE = {
  /** JHA customerType long form. Matches Personal cohort — Sole Prop IS a person. */
  customerType: 'Individual' as const,
  /** Single-char JHA short code surfaced on CustAdd. */
  customerTypeShort: 'I' as const,
  /** Core Director Field 92 / call class. Personal DBA partition. */
  callClass: '26' as const,
  /** Core Director Field 39 — Consumer flag. */
  cifField39: 'Y' as const,
  /** TIN code — Sole Prop always uses owner's SSN. */
  tinCode: 'S' as const,
  /** CIP Field 254 — ID source code for Fictitious Name Filing. */
  cipField254: '6' as const,
  /**
   * Account Field 84 suffix appended after the business name to produce the
   * canonical Sole Prop overlay (e.g., "Bill's Bait Shop DBA").
   */
  field84Suffix: ' DBA' as const,
} as const;

// -----------------------------------------------------------------------------
// Validation rules
// -----------------------------------------------------------------------------

export const SOLE_PROP_VALIDATION = {
  /** Core Director Field 84 max length (per CD field spec). */
  businessNameMaxLen: 60 - SOLE_PROP_WIRE.field84Suffix.length,
  /** SSN must be 9 digits (XX-XXXX). */
  ssnLen: 9,
  /** Fictitious Filing # — freeform but required (CIP source). */
  fictitiousFilingNumberMaxLen: 30,
} as const;

// -----------------------------------------------------------------------------
// Derived helpers
// -----------------------------------------------------------------------------

/**
 * Compute the Account Field 84 value (DBA overlay) from a raw business name.
 * Returns an empty string when the input is blank so the FSR sees the field
 * un-prefilled and the cart envelope stays clean.
 */
export function deriveField84(businessName: string | undefined | null): string {
  const trimmed = (businessName ?? '').trim();
  if (!trimmed) return '';
  return `${trimmed}${SOLE_PROP_WIRE.field84Suffix}`;
}

// -----------------------------------------------------------------------------
// US state options for Fictitious Filing state picker (same 50+DC as Address)
// -----------------------------------------------------------------------------

export const SOLE_PROP_STATE_OPTIONS: ReadonlyArray<{ code: string; name: string }> = [
  { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' }, { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' },
  { code: 'DC', name: 'District of Columbia' }, { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' }, { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' }, { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' }, { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' }, { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' }, { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' }, { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' }, { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' }, { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' }, { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' }, { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' }, { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' }, { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' }, { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' }, { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' }, { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' }, { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' }, { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' }, { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' }, { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' }, { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' },
];

// -----------------------------------------------------------------------------
// Photo-ID source options (Step 1 owner identification)
// -----------------------------------------------------------------------------

export interface PhotoIdOption {
  readonly value: string;
  readonly label: string;
}

export const PHOTO_ID_TYPES: ReadonlyArray<PhotoIdOption> = [
  { value: 'DL',        label: "Driver's License" },
  { value: 'StateID',   label: 'State ID Card' },
  { value: 'Passport',  label: 'US Passport' },
  { value: 'PassCard',  label: 'US Passport Card' },
  { value: 'MilitaryID', label: 'Military ID' },
];

// -----------------------------------------------------------------------------
// Citizenship statuses surfaced to Sole Prop owners
// -----------------------------------------------------------------------------

export const SOLE_PROP_CITIZENSHIP_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'US Citizen',          label: 'US Citizen' },
  { value: 'Permanent Resident',  label: 'Permanent Resident' },
  { value: 'Non-Resident Alien',  label: 'Non-Resident Alien' },
  { value: 'Other',               label: 'Other' },
];

// -----------------------------------------------------------------------------
// Statement preference options
// -----------------------------------------------------------------------------

export const STATEMENT_PREF_OPTIONS: ReadonlyArray<{ value: string; label: string; hint: string }> = [
  {
    value: 'electronic',
    label: 'Electronic statements',
    hint: 'Customer accesses statements through online banking. No paper mailings.',
  },
  {
    value: 'paper',
    label: 'Paper statements',
    hint: 'Paper statements mailed monthly to the address on file.',
  },
];

// -----------------------------------------------------------------------------
// NAICS helper text — placeholder; final picker is admin-config (see business-config.ts §NAICS_HELPER_TEXT)
// -----------------------------------------------------------------------------

export const SOLE_PROP_NAICS_HELPER =
  'Enter the 6-digit NAICS code for the primary business activity (Core Director Field 53). ' +
  'Examples: 722511 = Full-Service Restaurants, 541110 = Offices of Lawyers, 811111 = General Auto Repair.';
